import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, DollarSign, Users, CheckCircle, Clock, 
  AlertCircle, ArrowRight, PlusCircle, BarChart3, Target,
  Bell, AlertTriangle, PhoneCall, Calendar, ClipboardList,
  FileWarning, CalendarClock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CRMLayout from '../../components/CRM/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGE_COLORS = {
  lead: 'bg-blue-500', contacted: 'bg-yellow-500', application: 'bg-orange-500',
  submitted: 'bg-purple-500', approved: 'bg-green-500', declined: 'bg-red-500',
  docs_out: 'bg-teal-500', settled: 'bg-emerald-500', lost: 'bg-gray-500'
};

const STAGE_LABELS = {
  lead: 'New Lead', contacted: 'Contacted', application: 'Application',
  submitted: 'Submitted', approved: 'Approved', declined: 'Declined',
  docs_out: 'Docs Out', settled: 'Settled', lost: 'Lost'
};

export default function CRMDashboard() {
  const [stats, setStats] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, dealsRes, alertsRes, workloadRes] = await Promise.all([
        axios.get(`${API}/crm/dashboard`),
        axios.get(`${API}/crm/deals`),
        axios.get(`${API}/crm/alerts`),
        axios.get(`${API}/crm/settlement-workload`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentDeals(dealsRes.data.slice(0, 5));
      setAlerts(alertsRes.data);
      setWorkload(workloadRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      </CRMLayout>
    );
  }

  const alertCount = alerts?.total_alerts || 0;

  return (
    <CRMLayout alertCount={alertCount}>
      <div className="pb-20">
        {/* Header */}
        <div className="bg-navy-800 border-b border-white/5">
          <div className="px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading text-white uppercase tracking-wide" data-testid="dashboard-title">
                  FlashIQ Dashboard
                </h1>
                <p className="text-gray-400 mt-1">Flash Funding Deal Intelligence</p>
              </div>
              <div className="flex gap-3">
                <Link to="/crm/pipeline">
                  <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10" data-testid="pipeline-view-btn">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Pipeline View
                  </Button>
                </Link>
                <Link to="/crm/deals/new">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-deal-btn">
                    <PlusCircle className="h-4 w-4 mr-2" />
                    New Deal
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          {/* Alerts Banner */}
          {alertCount > 0 && (
            <div className="mb-8 space-y-3" data-testid="alerts-section">
              {alerts?.follow_ups?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                  <PhoneCall className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-yellow-400 font-medium text-sm">Follow-up Needed</p>
                    <div className="mt-1 space-y-1">
                      {alerts.follow_ups.slice(0, 3).map(fu => (
                        <Link key={fu.id} to={`/crm/deals/${fu.id}`} className="block text-gray-300 text-sm hover:text-white">
                          <span className="font-medium">{fu.client_name}</span>
                          <span className="text-gray-500"> — ${fu.amount?.toLocaleString('en-AU')} — waiting {fu.days_waiting} day{fu.days_waiting !== 1 ? 's' : ''}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {alerts?.stale_deals?.length > 0 && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-orange-400 font-medium text-sm">Stale Deals — No Activity in 3+ Days</p>
                    <div className="mt-1 space-y-1">
                      {alerts.stale_deals.slice(0, 3).map(sd => (
                        <Link key={sd.id} to={`/crm/deals/${sd.id}`} className="block text-gray-300 text-sm hover:text-white">
                          <span className="font-medium">{sd.client_name}</span>
                          <span className="text-gray-500"> — {STAGE_LABELS[sd.stage]} — {sd.days_stale} days inactive</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {alerts?.overdue_tasks?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                  <Bell className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-400 font-medium text-sm">Overdue Tasks</p>
                    <div className="mt-1 space-y-1">
                      {alerts.overdue_tasks.slice(0, 3).map(ot => (
                        <Link key={ot.id} to={`/crm/deals/${ot.deal_id}`} className="block text-gray-300 text-sm hover:text-white">
                          <span className="font-medium">{ot.title}</span>
                          <span className="text-gray-500"> — {ot.client_name} — {ot.days_overdue} day{ot.days_overdue !== 1 ? 's' : ''} overdue</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Deals"
              value={stats?.total_deals || 0}
              icon={<Users className="h-6 w-6" />}
              color="purple"
              testId="stat-total-deals"
            />
            <StatCard
              title="Pipeline Value"
              value={`$${(stats?.pipeline_value || 0).toLocaleString('en-AU')}`}
              icon={<DollarSign className="h-6 w-6" />}
              color="green"
              testId="stat-pipeline-value"
            />
            <StatCard
              title="Settled This Month"
              value={stats?.settled_this_month || 0}
              subtext={`$${(stats?.settled_value_this_month || 0).toLocaleString('en-AU')}`}
              icon={<CheckCircle className="h-6 w-6" />}
              color="emerald"
              testId="stat-settled"
            />
            <StatCard
              title="Conversion Rate"
              value={`${stats?.conversion_rate || 0}%`}
              icon={<Target className="h-6 w-6" />}
              color="blue"
              testId="stat-conversion"
            />
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard
              title="Avg Deal Size"
              value={`$${(stats?.avg_deal_size || 0).toLocaleString('en-AU')}`}
              icon={<TrendingUp className="h-6 w-6" />}
              color="yellow"
              small
              testId="stat-avg-deal"
            />
            <StatCard
              title="Pending Tasks"
              value={stats?.pending_tasks || 0}
              icon={<Clock className="h-6 w-6" />}
              color="orange"
              small
              testId="stat-pending-tasks"
            />
            <StatCard
              title="Active Leads"
              value={stats?.deals_by_stage?.lead || 0}
              icon={<AlertCircle className="h-6 w-6" />}
              color="red"
              small
              testId="stat-active-leads"
            />
          </div>

          {/* Settlement Workload Widget */}
          {workload && (
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6 mb-8" data-testid="settlement-workload-widget">
              <h2 className="text-xl font-heading text-white uppercase mb-5">Settlement Workload</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Nearing Settlement */}
                <WorkloadBlock
                  icon={<Calendar className="h-5 w-5" />}
                  color="teal"
                  title="Nearing Settlement"
                  subtitle="Next 7 days"
                  count={workload.nearing_settlement?.count || 0}
                  testId="workload-nearing"
                >
                  {(workload.nearing_settlement?.items || []).map(item => (
                    <Link key={item.deal_id} to={`/crm/deals/${item.deal_id}/workspace`}
                      className="flex items-center justify-between py-1.5 text-xs hover:text-white transition group">
                      <span className="text-gray-300 group-hover:text-white truncate flex-1">{item.client_name}</span>
                      <span className="text-gray-500 ml-2 shrink-0">{new Date(item.settlement_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                    </Link>
                  ))}
                </WorkloadBlock>

                {/* Overdue Checklists */}
                <WorkloadBlock
                  icon={<ClipboardList className="h-5 w-5" />}
                  color="red"
                  title="Overdue Checklist"
                  subtitle="Incomplete items"
                  count={workload.overdue_checklists?.count || 0}
                  countLabel="deals"
                  testId="workload-overdue-checklist"
                >
                  {(workload.overdue_checklists?.items || []).map(item => (
                    <Link key={item.deal_id} to={`/crm/deals/${item.deal_id}/workspace`}
                      className="flex items-center justify-between py-1.5 text-xs hover:text-white transition group">
                      <span className="text-gray-300 group-hover:text-white truncate flex-1">{item.client_name}</span>
                      <span className="text-red-400/70 ml-2 shrink-0 text-[10px]">{item.incomplete_count} items</span>
                    </Link>
                  ))}
                </WorkloadBlock>

                {/* Open Conditions */}
                <WorkloadBlock
                  icon={<FileWarning className="h-5 w-5" />}
                  color="amber"
                  title="Open Conditions"
                  subtitle="Across submissions"
                  count={workload.open_conditions?.count || 0}
                  countLabel="conditions"
                  testId="workload-conditions"
                >
                  {(workload.open_conditions?.items || []).map(item => (
                    <Link key={item.deal_id} to={`/crm/deals/${item.deal_id}/workspace`}
                      className="flex items-center justify-between py-1.5 text-xs hover:text-white transition group">
                      <span className="text-gray-300 group-hover:text-white truncate flex-1">{item.client_name}</span>
                      <span className="text-amber-400/70 ml-2 shrink-0 text-[10px]">{item.open_count} open</span>
                    </Link>
                  ))}
                </WorkloadBlock>

                {/* Upcoming Follow-ups */}
                <WorkloadBlock
                  icon={<CalendarClock className="h-5 w-5" />}
                  color="purple"
                  title="Follow-ups Due"
                  subtitle="Next 7 days"
                  count={workload.upcoming_followups?.count || 0}
                  countLabel="tasks"
                  testId="workload-followups"
                >
                  {(workload.upcoming_followups?.items || []).map(item => (
                    <Link key={item.task_id} to={`/crm/deals/${item.deal_id}/workspace`}
                      className="flex items-center justify-between py-1.5 text-xs hover:text-white transition group">
                      <span className="text-gray-300 group-hover:text-white truncate flex-1">{item.client_name}</span>
                      <span className="text-gray-500 ml-2 shrink-0">{new Date(item.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                    </Link>
                  ))}
                </WorkloadBlock>
              </div>
            </div>
          )}

          {/* Pipeline Overview */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6 mb-8" data-testid="pipeline-overview">
            <h2 className="text-xl font-heading text-white uppercase mb-6">Pipeline Overview</h2>
            <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
              {Object.entries(stats?.deals_by_stage || {}).map(([stage, count]) => (
                <Link 
                  key={stage} 
                  to={`/crm/pipeline?stage=${stage}`}
                  className="text-center group"
                >
                  <div className={`${STAGE_COLORS[stage]} rounded-lg p-3 mb-2 group-hover:scale-105 transition-transform`}>
                    <span className="text-2xl font-bold text-white">{count}</span>
                  </div>
                  <span className="text-xs text-gray-400 group-hover:text-white transition-colors">
                    {STAGE_LABELS[stage]}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Deals */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6" data-testid="recent-deals">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading text-white uppercase">Recent Deals</h2>
              <Link to="/crm/pipeline" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                View All <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            
            {recentDeals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No deals yet</p>
                <Link to="/crm/deals/new">
                  <Button className="bg-purple-600 hover:bg-purple-700" data-testid="create-first-deal-btn">
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Create Your First Deal
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentDeals.map((deal) => (
                  <Link 
                    key={deal.id}
                    to={`/crm/deals/${deal.id}`}
                    className="flex items-center justify-between p-4 bg-navy-900 rounded-lg hover:bg-navy-900/80 transition-colors group"
                    data-testid={`recent-deal-${deal.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${STAGE_COLORS[deal.stage]}`}></div>
                      <div>
                        <p className="text-white font-medium group-hover:text-purple-400 transition-colors">
                          {deal.client_name}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {deal.business_name} · {deal.finance_type} · ${deal.amount?.toLocaleString('en-AU')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded text-xs ${STAGE_COLORS[deal.stage]} text-white`}>
                        {STAGE_LABELS[deal.stage]}
                      </span>
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(deal.created_at).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </CRMLayout>
  );
}

function StatCard({ title, value, subtext, icon, color, small, testId }) {
  const colorClasses = {
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-400',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30 text-red-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl ${small ? 'p-4' : 'p-6'}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{title}</span>
        <span className={colorClasses[color].split(' ').pop()}>{icon}</span>
      </div>
      <p className={`${small ? 'text-2xl' : 'text-3xl'} font-heading text-white`}>{value}</p>
      {subtext && <p className="text-gray-400 text-sm mt-1">{subtext}</p>}
    </div>
  );
}


function WorkloadBlock({ icon, color, title, subtitle, count, countLabel, children, testId }) {
  const colors = {
    teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/20', text: 'text-teal-400', count: 'text-teal-300' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', count: 'text-red-300' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', count: 'text-amber-300' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', count: 'text-purple-300' },
  };
  const c = colors[color] || colors.teal;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <span className={c.text}>{icon}</span>
        <span className="text-white text-sm font-medium">{title}</span>
      </div>
      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">{subtitle}</p>
      <p className={`text-2xl font-heading ${c.count} mb-3`}>
        {count} <span className="text-sm text-gray-500 font-normal">{countLabel || ''}</span>
      </p>
      <div className="border-t border-white/5 pt-2 space-y-0">
        {children}
        {(!children || (Array.isArray(children) && children.length === 0)) && (
          <p className="text-gray-600 text-xs py-1">None</p>
        )}
      </div>
    </div>
  );
}
